import { BaseAttribute } from '@gumtree/shared/src/types/attributes';
import {
    getL1CategoryIdBySeoName,
    getL2CategoryIdBySeoName,
} from '@gumtree/shared/src/util/category-mapping';

const getAttribute = (adDetails, attributeName) => {
    return adDetails.attributes?.find((attribute) => attribute.key === attributeName);
};

const shouldAdContainSchema = (adDetails) => {
    if (!adDetails) {
        return false;
    }

    // All pets listings should not contain a schema, apart from equipment and accessories
    if (
        adDetails.l1Category === getL1CategoryIdBySeoName('pets') &&
        adDetails.l2CategoryId !== getL2CategoryIdBySeoName('pet-equipment-accessories')
    ) {
        return false;
    }

    // Only 'For Sale', 'To Rent' and 'International' categories within property should contain a schema.
    if (adDetails.l1Category === getL1CategoryIdBySeoName('flats-houses')) {
        if (
            [
                getL2CategoryIdBySeoName('property-for-sale'),
                getL2CategoryIdBySeoName('property-to-rent'),
                getL2CategoryIdBySeoName('international-property-for-sale'),
            ].includes(adDetails.l2CategoryId)
        ) {
            return true;
        }

        return false;
    }

    const excludedL1Categories = [
        getL1CategoryIdBySeoName('business-services'),
        getL1CategoryIdBySeoName('community'),
    ];
    if (excludedL1Categories.includes(adDetails.l1Category)) {
        return false;
    }

    const excludedL2Categories = [
        getL2CategoryIdBySeoName('stuff-wanted'),
        getL2CategoryIdBySeoName('cars-wanted'),
        getL2CategoryIdBySeoName('jobs-wanted'),
        getL2CategoryIdBySeoName('property-wanted'),
    ];
    if (excludedL2Categories.includes(adDetails.l2CategoryId)) {
        return false;
    }

    return true;
};

const getPrice = (adDetails) => {
    return adDetails.seoPrice ?? 0;
};

const getPopulatedRealEstateSchema = (adDetails, url) => {
    const propertyType = getAttribute(adDetails, 'property_type');
    const numberOfBedrooms = getAttribute(adDetails, 'property_number_beds');

    const getSchemaTypes = () => {
        const schemaTypes = ['Product', 'RealEstateListing'];

        switch (propertyType?.value) {
            case 'House':
                schemaTypes.push('House');
                break;
            case 'Flat':
                schemaTypes.push('Apartment');
                break;
        }

        if (adDetails.l2CategoryId === getL2CategoryIdBySeoName('property-to-rent')) {
            schemaTypes.push('RentAction');
        }

        return schemaTypes;
    };

    return {
        '@context': 'https://schema.org',
        '@type': getSchemaTypes(),
        numberOfBedrooms: numberOfBedrooms?.value,
        address: adDetails.address,
        description: adDetails.description?.join(' '),
        name: adDetails.title,
        url,
        sku: adDetails.id,
        offers: {
            '@type': 'Offer',
            priceCurrency: 'GBP',
            price: getPrice(adDetails),
        },
        datePosted: adDetails.postedDate ? new Date(adDetails.postedDate) : undefined,
    };
};

const getPopulatedProductSchema = (adDetails) => {
    return {
        '@context': 'https://schema.org',
        '@type': 'Product',
        sku: adDetails.id,
        name: adDetails.title,
        description: adDetails.description?.join(' '),
        offers: {
            '@type': 'Offer',
            priceCurrency: 'GBP',
            price: getPrice(adDetails),
            availableAtOrFrom: adDetails.address,
        },
    };
};

const getPopulatedJobPostingSchema = (adDetails) => {
    const hiringOrganization = getAttribute(adDetails, 'recruiter');

    return {
        '@context': 'https://schema.org',
        '@type': 'JobPosting',
        title: adDetails.title,
        description: adDetails.description?.join(' '),
        hiringOrganization: hiringOrganization?.value,
        jobLocation: {
            address: adDetails.address,
        },
        datePosted: adDetails.postedDate ? new Date(adDetails.postedDate) : undefined,
    };
};

export function getCarAttribute(attributes: BaseAttribute[]) {
    return (key: string) => {
        return attributes.find((i) => i.key === key)?.value;
    };
}

function setNotNullAttribute(schema, key, value) {
    if (value !== null && value !== undefined) {
        schema[key] = value;
    }
}

export const getPopulatedSchema = (adDetails, images, url, domain) => {
    if (!shouldAdContainSchema(adDetails)) {
        return null;
    }
    let schema;

    if (adDetails.l1Category === getL1CategoryIdBySeoName('flats-houses')) {
        schema = getPopulatedRealEstateSchema(adDetails, url);
    } else if (adDetails.seoL1Category === 'Jobs') {
        schema = getPopulatedJobPostingSchema(adDetails);
    } else {
        schema = getPopulatedProductSchema(adDetails);
    }

    // The cars schema has the attribution as the product schema, but with a different type.
    if (adDetails.seoL2Category === 'Cars') {
        schema['@type'] = 'Product';
        schema.offers.availability = 'https://schema.org/InStock';
        schema.itemCondition = 'https://schema.org/UsedCondition';
        schema.mileageFromodometer = {
            '@type': 'QuantitativeValue',
            unitCode: 'SMI',
        };
        schema.brand = {
            '@type': 'Brand',
        };
        const getAttributeByKey = getCarAttribute(adDetails.allAttributes ?? []);
        // year
        setNotNullAttribute(
            schema,
            'vehicleModelDate',
            getAttributeByKey('vehicle_registration_year')
        );
        // miles
        setNotNullAttribute(
            schema.mileageFromodometer,
            'value',
            getAttributeByKey('vehicle_mileage')?.replace(/,/g, '')
        );
        // model
        setNotNullAttribute(schema, 'model', getAttributeByKey('vehicle_model'));
        // brand
        setNotNullAttribute(schema.brand, 'name', getAttributeByKey('vehicle_make'));
    }

    if (images && images.length > 0) {
        schema.image = images;
    }

    if (url && schema.offers) {
        schema.offers.url = url;
    }

    if (domain && schema.offers) {
        schema.offers.offeredBy = {
            '@id': `https://${domain}${adDetails.sellerOtherAds}`,
        };
    }

    return schema;
};
